import { Info } from "@tamagui/lucide-icons";
import { action, makeObservable } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../activities/types";
import type { Api } from "../../core/apis";
import type { Stores } from "../../core/stores";
import BaseModuleModel, { type ModuleModelData } from "../core/BaseModuleModel";
import { InfoModuleBlockUI, InfoModuleUI } from "./InfoModuleUI";

export type InfoModuleSnapshot = {
  text: string;
};

export interface InfoModuleModelData
  extends ModuleModelData<InfoModuleSnapshot> {
  text: string;
}

export default class InfoModuleModel extends BaseModuleModel<
  InfoModuleSnapshot,
  InfoModuleModelData
> {
  constructor(stores: Stores, api: Api, data: InfoModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      text: data.text ?? "",
    });

    makeObservable(this);
  }

  @action async updateSnapshotWithoutSave() {
    this.data.snapshot = { text: this.data.text };
  }

  setText = (text: string) => {
    const oldValue = this.data.text;
    if (oldValue !== text) {
      this.updateTimestampWithoutSave();
      this.data.text = text;
      this.api.modulesApi.set(this);

      this.postActivityEvent(
        ActivityEventType.Edited,
        TKey.ActivityInfoTextUpdated,
        {
          oldValue,
          newValue: this.data.text ?? "",
        },
      );
      this.sendTelemetryEvent("text_updated");
    }
  };

  toFireStore(): Omit<InfoModuleModelData, "uid"> {
    return {
      ...super.toFireStore(),
      text: this.data.text,
    };
  }

  render() {
    return <InfoModuleUI module={this} />;
  }

  renderStatic() {
    return null;
  }

  renderBlock() {
    return <InfoModuleBlockUI module={this} />;
  }

  renderStaticBlock() {
    return this.renderBlock();
  }

  renderIcon(size?: number) {
    return <Info size={size} />;
  }
}
